import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Calendar,
  Clock,
  AppWindow,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
  AlertCircle,
  RefreshCw,
  User,
  Link2,
  Monitor,
  FileText,
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatUtcTime } from '@/utils/dateUtils';

function AppUrlDetailsPage() {
  const { t, isRTL } = useLanguage();
  const { staffId, appName } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const domain = searchParams.get('domain') || '';
  const date = searchParams.get('date') || new Date().toISOString().split('T')[0];
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('app_name', decodeURIComponent(appName));
      if (domain) params.append('domain', domain);
      if (date) params.append('date', date);
      
      const res = await axios.get(
        `${API}/admin/usage-reports/staff/${staffId}/app-details?${params.toString()}`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch (error) {
      console.error('Error fetching app details:', error);
      toast.error('Failed to load URL details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (staffId && appName) {
      fetchData();
    }
  }, [staffId, appName, domain, date]);

    const formatTime = (value) => formatUtcTime(value);


  const formatDate = (isoString) => {
    if (!isoString) return '-';
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '-';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <AppWindow className="w-6 h-6 text-purple-600" />
              {decodeURIComponent(appName)} - URL Details
            </h1>
            <p className="text-gray-500 flex items-center gap-2">
              <User className="w-4 h-4" />
              {data?.staff?.name || 'Staff'} · {data?.staff?.email}
              {domain && (
                <>
                  <span className="mx-2">·</span>
                  <Globe className="w-4 h-4" />
                  {domain}
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm">{date}</span>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Time</p>
                <p className="text-2xl font-bold">{data?.total_duration_formatted || '0s'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Link2 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Sessions</p>
                <p className="text-2xl font-bold">{data?.total_records || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <AppWindow className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Application</p>
                <p className="text-xl font-bold truncate">{decodeURIComponent(appName)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* URL Details Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-600" />
            URL Activity Log
          </CardTitle>
          <CardDescription>
            Detailed browsing history for {decodeURIComponent(appName)} on {date}
            <br />
            <span className="text-xs text-gray-400 italic">
              * URLs are partially masked for privacy protection
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!data?.url_details || data.url_details.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Globe className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No URL activity found for this app on the selected date</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">URL (Masked)</TableHead>
                    <TableHead className="w-[200px]">Window Title</TableHead>
                    <TableHead>Start Time</TableHead>
                    <TableHead>End Time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>Device</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.url_details.map((item, index) => (
                    <TableRow key={item.usage_id || index}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-mono text-gray-600 truncate max-w-[200px]" title={item.site_url_masked}>
                            {item.site_url_masked || '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-gray-600 truncate max-w-[180px]" title={item.window_title}>
                            {item.window_title || '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{formatTime(item.start_time)}</p>
                          <p className="text-xs text-gray-400">{formatDate(item.start_time)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{formatTime(item.end_time)}</p>
                          <p className="text-xs text-gray-400">{formatDate(item.end_time)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-700">
                          {item.duration_formatted}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.policy_status === 'block' ? (
                          <Badge className="bg-red-100 text-red-700">
                            <XCircle className="w-3 h-3 mr-1" />
                            Block
                          </Badge>
                        ) : item.policy_status === 'allow' ? (
                          <Badge className="bg-indigo-100 text-indigo-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Allow
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Unlisted
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Monitor className="w-4 h-4" />
                          <span className="truncate max-w-[100px]" title={item.device_id}>
                            {item.device_id?.slice(0, 12) || '-'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AppUrlDetailsPage;
